import PropTypes from 'prop-types'
import SearchInput from "./SearchInput"
import AppContext from "../services/context.js"
import { useContext, useState } from "react"
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import AvatarMenu from './AvatarMenu'
import { trackEvent } from '../lib/analytics'

const Header = ({ withLogo, showSearchInput }) => {
  const {
    searchResults,
    individualResult,
    loading,
    userState,
    userActions,
    themeKey,
    setSearchTerm,
    setSearchResults,
    setIndividualResult,
    setIsAiMode,
    setAiResponse,
  } = useContext(AppContext)
  const showingResults = Boolean(searchResults.length || individualResult.title)
  const isDarkTheme = themeKey === 'dark'
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleLogoClick = () => {
    setSearchTerm("");
    setSearchResults([]);
    setIndividualResult({});
    setIsAiMode(false);
    setAiResponse(null);
  };
  const userInitial = String(userState?.profile?.fullName || userState?.profile?.email || '?')
    .trim()
    .slice(0, 1)
    .toUpperCase()

  const handlePlanSwitch = (plan) => {
    const result = userActions.switchPlan(plan)
    trackEvent('plan_selected', {
      plan,
      source: 'avatar_menu',
      authenticated: true,
      current_plan: userState?.currentPlan || 'none',
    })
    return { changed: result?.success }
  }

  const shouldCenterHeader = showSearchInput && !showingResults && !loading
  const shouldRenderTopBar = withLogo

  return (
    <header className={clsx(
      "w-screen overflow-x-clip text-center mt-4 transition-all duration-500 relative flex flex-col items-center",
      {
        "mt-[calc(50vh-210px)]": shouldCenterHeader,
        "pt-1.5": !loading
      }
    )}>
      <div
        className={clsx(
          "w-full max-w-5xl px-4 transition-all duration-500 ease-in-out overflow-hidden",
          shouldRenderTopBar
            ? "max-h-[150px] opacity-100"
            : "max-h-0 opacity-0 pointer-events-none"
        )}
      >
        <div className="flex justify-center pt-2 pb-6">
          <Link
            to="/"
            onClick={handleLogoClick}
            className="cursor-pointer"
            aria-label="Voltar para a busca"
          >
            <img src="/icon.webp" alt="GreenBook Icon" className="h-[70px] cursor-pointer" />
          </Link>
        </div>
      </div>
      {showSearchInput && (
        <div className="w-full max-w-[720px] mx-auto flex items-start px-4 sm:px-0">
          {/* Left Slot: contains the compact logo or spacer */}
          <div
            className={clsx(
              "transition-all duration-500 ease-in-out flex-shrink-0 mt-2 overflow-hidden flex items-center justify-center",
              showingResults
                ? "w-11 opacity-100 mr-2 sm:mr-3"
                : userState.isAuthenticated
                  ? "md:w-11 w-0 opacity-0 md:mr-3 mr-0"
                  : "w-0 opacity-0 mr-0"
            )}
          >
            <Link
              to="/"
              onClick={handleLogoClick}
              className="cursor-pointer"
              aria-label="Voltar para a busca"
            >
              <img src="/icon.webp" alt="GreenBook Icon" className="h-11 w-11 object-contain" />
            </Link>
          </div>

          {/* Search Input: expands/contracts smoothly */}
          <div className="flex-grow min-w-0">
            <SearchInput mobileActionsEnd={userState.isAuthenticated ? (
              <div className="relative">
                <button
                  type="button"
                  className={clsx(
                    "h-10 w-10 rounded-full flex items-center justify-center font-semibold shadow transition-colors cursor-pointer",
                    isDarkTheme
                      ? "bg-greenbook-dark-primary text-greenbook-dark-toggle-text hover:bg-greenbook-light-green"
                      : "bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900"
                  )}
                  onClick={() => setIsMenuOpen((value) => !value)}
                  aria-label="Abrir menu do usuário"
                >
                  {userInitial}
                </button>
                {isMenuOpen && (
                  <AvatarMenu
                    profile={userState.profile}
                    onSwitchPlan={handlePlanSwitch}
                    onLogout={() => {
                      userActions.logout();
                      setIsMenuOpen(false);
                    }}
                    isDarkTheme={isDarkTheme}
                  />
                )}
              </div>
            ) : null} />
          </div>

          {/* Right Slot: avatar — desktop only (hidden on mobile, shown below search) */}
          <div
            className={clsx(
              "hidden sm:flex transition-all duration-500 ease-in-out flex-shrink-0 mt-2",
              userState.isAuthenticated
                ? "w-11 opacity-100 ml-3"
                : "w-0 opacity-0 ml-0"
            )}
          >
            {userState.isAuthenticated && (
              <div className="relative">
                <button
                  type="button"
                  className={clsx(
                    "h-11 w-11 rounded-full flex items-center justify-center font-semibold shadow transition-colors cursor-pointer",
                    isDarkTheme
                      ? "bg-greenbook-dark-primary text-greenbook-dark-toggle-text hover:bg-greenbook-light-green"
                      : "bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900"
                  )}
                  onClick={() => setIsMenuOpen((value) => !value)}
                  aria-label="Abrir menu do usuário"
                >
                  {userInitial}
                </button>
                {isMenuOpen && (
                  <AvatarMenu
                    profile={userState.profile}
                    onSwitchPlan={handlePlanSwitch}
                    onLogout={() => {
                      userActions.logout();
                      setIsMenuOpen(false);
                    }}
                    isDarkTheme={isDarkTheme}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  )
}

Header.propTypes = {
  withLogo: PropTypes.bool,
  showSearchInput: PropTypes.bool,
}

Header.defaultProps = {
  withLogo: true,
  showSearchInput: true,
}

export default Header
